/**
 * Build Your First Agent Tour - Step definitions and configuration
 * Simulates the complete workflow execution flow
 */

import i18next from "i18next";
import { type TourStep } from "#app/components/tours/tour-provider";
import {
  activateTourMode,
  deactivateTourMode,
  getMockWorkflow,
  getRandomTourPromptKey,
  getTourPromptText,
  getMockWorkflowForKey,
  createMockMessages,
  simulatePlanningPhase,
  getWorkflowLifecycleResponse,
  getMockResponse,
  ensureTourI18nReady,
} from "./sandbox-simulator";

const TOUR_ID = "build-first-agent";

/**
 * Tour step definitions - realistic workflow simulation
 */
function getTourSteps(
  onNavigate: (path: string) => void,
  onInjectMockData: (
    messages: any[],
    workflow: any,
    options?: {
      showPlanning?: boolean;
      thinking?: string | null;
      markStreamingComplete?: boolean;
    },
  ) => void,
  onUpdateThinking: (text: string | null) => void,
  onAddTodo: (todo: {
    id: string;
    text: string;
    status: "started" | "completed" | "failed";
  }) => void,
  onCompleteTodo: (id: string) => void,
  onSetActivityRunStatus: (status: any[]) => void,
  onSetWorkflowRunStatus: (
    status: "running" | "completed" | "failed",
    runId?: string | null,
  ) => void,
  onMarkStreamingComplete: () => void,
  onInjectWorkflow: (workflow: any, finalMessage: string) => void,
): TourStep[] {
  // Get random prompt for this tour session
  const tourPromptKey = getRandomTourPromptKey();
  const tourPrompt = getTourPromptText(tourPromptKey);
  const workflow =
    getMockWorkflowForKey(tourPromptKey) ?? getMockWorkflow(tourPrompt);
  const tt = (key: string, opts?: Record<string, unknown>) =>
    i18next.t(key, { ns: "settings", ...opts });

  return [
    // Step 0: Introduction to chat input (automatically shown after navigation)
    {
      id: "chat-input-intro",
      attachTo: {
        element: '[data-tour="chat-input"]',
        on: "bottom",
      },
      beforeShowPromise: () => {
        // Wait for the element to be available after navigation
        return new Promise((resolve) => {
          const checkElement = () => {
            const element = document.querySelector('[data-tour="chat-input"]');
            if (element) {
              resolve(undefined);
            } else {
              setTimeout(checkElement, 100);
            }
          };
          checkElement();
        });
      },
      text: `<div class="tour-step-with-icon"><img src="/img/tour.svg" alt="" class="tour-modal-icon" /><p class="tour-step-body">${tt("tour.chatInputIntro", { prompt: tourPrompt })}</p></div>`,

      buttons: [
        {
          text: tt("tour.sendPrompt"),
          action: (tourCtx: any) => {
            const advanceToNext = () => {
              try {
                if (tourCtx && typeof tourCtx.next === "function") {
                  tourCtx.next();
                }
              } catch (error) {
                console.error("Error advancing tour:", error);
              }
            };

            void (async () => {
              await ensureTourI18nReady();

              onInjectMockData([{ role: "user", content: tourPrompt }], null, {
                showPlanning: false,
              });

              setTimeout(() => {
                const lifecycleResponse =
                  getWorkflowLifecycleResponse(tourPrompt);

                onInjectMockData(
                  [
                    {
                      role: "assistant",
                      content: lifecycleResponse,
                      isStreaming: false,
                    },
                  ],
                  null,
                  { showPlanning: false },
                );

                setTimeout(() => {
                  advanceToNext();
                }, 1500);
              }, 500);
            })();
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 1: Show planning phase happening (automatically starts when step shows)
    {
      id: "planning-phase",
      attachTo: {
        element: '[data-tour="chat-input"]',
        on: "bottom",
      },
      beforeShowPromise: () => {
        // Wait for step to show first, then start planning
        return new Promise((resolve) => {
          // Small delay to let the step UI render
          setTimeout(() => {
            resolve(undefined);

            // Start planning phase after step is shown
            setTimeout(() => {
              const assistantMsg = {
                role: "assistant" as const,
                content: "",
                isStreaming: true,
              };

              onUpdateThinking(tt("tour.thinking.0"));
              onInjectMockData([assistantMsg], null, {
                showPlanning: true,
                thinking: tt("tour.thinking.0"),
              });

              // Simulate planning phase with progressive to-dos
              void simulatePlanningPhase(
                onUpdateThinking,
                onAddTodo,
                onCompleteTodo,
              )
                .then(() => {
                  // After planning, automatically inject final workflow
                  setTimeout(() => {
                    onUpdateThinking(null);

                    // Mark previous streaming message as complete first
                    onMarkStreamingComplete();

                    // Then add final message with workflow
                    setTimeout(() => {
                      if (!workflow) {
                        return;
                      }

                      const finalMessages = createMockMessages(
                        tourPrompt,
                        workflow,
                        false,
                      );
                      const finalMessage = finalMessages.find(
                        (m) => m.role === "assistant" && m.workflow,
                      );

                      if (finalMessage) {
                        onInjectWorkflow(workflow, finalMessage.content);
                      } else {
                        onInjectWorkflow(workflow, getMockResponse(tourPrompt));
                      }
                    }, 300);
                  }, 500);
                })
                .catch((error) => {
                  console.error("Tour planning simulation failed:", error);
                });
            }, 300);
          }, 100);
        });
      },
      text: tt("tour.planningPhaseStep"),
      buttons: [
        {
          text: tt("tour.continue"),
          action: (tourCtx: any) => {
            // Wait for workflow canvas to be available
            const checkWorkflowReady = () => {
              const canvasElement = document.querySelector(
                '[data-tour="workflow-canvas"]',
              );
              if (canvasElement) {
                tourCtx.next();
              } else {
                setTimeout(checkWorkflowReady, 500);
              }
            };
            // Give it more time for workflow to generate
            setTimeout(checkWorkflowReady, 2000);
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 1: Show workflow generation complete (workflow already generated automatically)
    {
      id: "workflow-generated",
      attachTo: {
        element: '[data-tour="workflow-canvas"]',
        on: "left",
      },
      beforeShowPromise: () => {
        return new Promise((resolve) => {
          // Wait for workflow canvas to be available
          const checkElement = () => {
            const element = document.querySelector(
              '[data-tour="workflow-canvas"]',
            );
            if (element) {
              resolve(undefined);
            } else {
              setTimeout(checkElement, 100);
            }
          };
          checkElement();
        });
      },
      text: tt("tour.workflowGeneratedStep"),
      buttons: [
        {
          text: tt("tour.next"),
          action: (tourCtx: any) => {
            tourCtx.next();
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 3: Show input form (if workflow has inputs)
    {
      id: "input-form",
      attachTo: {
        element: '[data-tour="canvas-tab-input"]',
        on: "bottom",
      },
      text: tt("tour.inputFormStep"),
      buttons: [
        {
          text: tt("tour.showInputForm"),
          action: (tourCtx: any) => {
            // Switch to Input tab in canvas panel
            const el = document.querySelector(
              '[data-tour="canvas-tab-input"]',
            ) as HTMLElement | null;
            el?.click();

            setTimeout(() => {
              tourCtx.next();
            }, 800);
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 4: Show execution starting
    {
      id: "execution-start",
      attachTo: {
        element: '[data-tour="workflow-canvas"]',
        on: "left",
      },
      text: tt("tour.executionStartStep"),
      buttons: [
        {
          text: tt("tour.startExecution"),
          action: (tourCtx: any) => {
            // Simulate workflow execution
            onSetWorkflowRunStatus("running", "tour-run-1");
            onSetActivityRunStatus([
              {
                stepId: "step_1",
                status: "running",
                activityRunId: "tour-activity-1",
                startedAt: new Date().toISOString(),
              },
            ]);

            setTimeout(() => {
              // Simulate step 1 completing and step 2 starting
              onSetActivityRunStatus([
                {
                  stepId: "step_1",
                  status: "completed",
                  activityRunId: "tour-activity-1",
                  outputs: {
                    stdout: tt("tour.executionMock.summaryGenerated"),
                    displayData: {
                      summary: tt("tour.executionMock.summaryContent"),
                    },
                  },
                  startedAt: new Date(Date.now() - 2000).toISOString(),
                  updatedAt: new Date().toISOString(),
                },
                {
                  stepId: "step_2",
                  status: "running",
                  activityRunId: "tour-activity-2",
                  startedAt: new Date().toISOString(),
                },
              ]);
              tourCtx.next();
            }, 1500);
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 5: Show audit trail with logs
    {
      id: "audit-trail",
      attachTo: {
        element: '[data-tour="canvas-tab-audit"]',
        on: "bottom",
      },
      text: tt("tour.auditTrailStep"),
      buttons: [
        {
          text: tt("tour.showAuditTrail"),
          action: (tourCtx: any) => {
            // Complete the workflow execution
            onSetWorkflowRunStatus("completed", "tour-run-1");
            onSetActivityRunStatus([
              {
                stepId: "step_1",
                status: "completed",
                activityRunId: "tour-activity-1",
                outputs: {
                  stdout: tt("tour.executionMock.summaryGenerated"),
                  displayData: {
                    summary: tt("tour.executionMock.summaryContent"),
                  },
                },
                startedAt: new Date(Date.now() - 3000).toISOString(),
                updatedAt: new Date(Date.now() - 1000).toISOString(),
              },
              {
                stepId: "step_2",
                status: "completed",
                activityRunId: "tour-activity-2",
                outputs: {
                  stdout: tt("tour.executionMock.emailSent"),
                  displayData: {
                    emailSent: true,
                    recipient: tt("tour.executionMock.recipient"),
                  },
                },
                startedAt: new Date(Date.now() - 1000).toISOString(),
                updatedAt: new Date().toISOString(),
              },
            ]);

            setTimeout(() => {
              const el = document.querySelector(
                '[data-tour="canvas-tab-audit"]',
              ) as HTMLElement | null;
              el?.click();
              tourCtx.next();
            }, 1000);
          },
          classes: "tour-next-btn",
        },
      ],
    },
    // Step 6: Completion message
    {
      id: "tour-complete",
      attachTo: {
        element: '[data-tour="workflow-canvas"]',
        on: "left",
      },
      text: tt("tour.tourCompleteStep"),
      buttons: [
        {
          text: tt("tour.completeTour"),
          action: (tourCtx: any) => {
            tourCtx.complete();
          },
          classes: "tour-next-btn",
        },
      ],
    },
  ];
}

/**
 * Build tour configuration
 */
export function buildTourConfig(
  onNavigate: (path: string) => void,
  onInjectMockData: (
    messages: any[],
    workflow: any,
    options?: {
      showPlanning?: boolean;
      thinking?: string | null;
      markStreamingComplete?: boolean;
    },
  ) => void,
  onUpdateThinking: (text: string | null) => void,
  onAddTodo: (todo: {
    id: string;
    text: string;
    status: "started" | "completed" | "failed";
  }) => void,
  onCompleteTodo: (id: string) => void,
  onSetActivityRunStatus: (status: any[]) => void,
  onSetWorkflowRunStatus: (
    status: "running" | "completed" | "failed",
    runId?: string | null,
  ) => void,
  onMarkStreamingComplete: () => void,
  onInjectWorkflow: (workflow: any, finalMessage: string) => void,
) {
  return {
    id: TOUR_ID,
    steps: getTourSteps(
      onNavigate,
      onInjectMockData,
      onUpdateThinking,
      onAddTodo,
      onCompleteTodo,
      onSetActivityRunStatus,
      onSetWorkflowRunStatus,
      onMarkStreamingComplete,
      onInjectWorkflow,
    ),
    beforeStart: () => {
      activateTourMode();
      // Automatically navigate to agent page when tour starts
      onNavigate("/u/alpha/agent");
    },
    afterComplete: () => {
      deactivateTourMode();
      // Navigate back to my agents
      onNavigate("/u/alpha/my-agents");
    },
    afterCancel: () => {
      deactivateTourMode();
    },
  };
}
